/**
 * Which languages the marketing site is served in, and what counts as a
 * translatable piece of text.
 *
 * The dictionary is keyed by the English text itself rather than by a code like
 * `hero.title`. That is the whole reason this survives content changes: edit a
 * service description, publish an article from the dashboard, add a section —
 * the new wording is simply a key nobody has seen before, and it gets picked up
 * and translated. A code-keyed catalogue would need a developer every time a
 * sentence changed, and would silently keep showing the old translation for the
 * new English.
 */

export const LANGS = ['en', 'lt'] as const;
export type Lang = (typeof LANGS)[number];

export const DEFAULT_LANG: Lang = 'en';

export const LANG_LABEL: Record<Lang, string> = { en: 'EN', lt: 'LT' };
export const LANG_NAME: Record<Lang, string> = { en: 'English', lt: 'Lietuvių' };

/** The header the middleware sets so a server component knows the language. */
export const LANG_HEADER = 'x-noventi-lang';

/** Remembers the choice so a visitor who picked Lithuanian keeps it. */
export const LANG_COOKIE = 'noventi_lang';

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

/** '/services' → '/lt/services'; the default language keeps the bare path. */
export function pathForLang(path: string, lang: Lang): string {
  const bare = stripLang(path);
  if (lang === DEFAULT_LANG) return bare;
  return `/${lang}${bare === '/' ? '' : bare}`;
}

/** '/lt/services' → '/services'. */
export function stripLang(path: string): string {
  for (const lang of LANGS) {
    if (lang === DEFAULT_LANG) continue;
    if (path === `/${lang}`) return '/';
    if (path.startsWith(`/${lang}/`)) return path.slice(lang.length + 1);
  }
  return path || '/';
}

/** The language a path is asking for. */
export function langFromPath(path: string): Lang {
  for (const lang of LANGS) {
    if (lang === DEFAULT_LANG) continue;
    if (path === `/${lang}` || path.startsWith(`/${lang}/`)) return lang;
  }
  return DEFAULT_LANG;
}

/* Paths that are never translated and never get a /lt twin: the dashboard is
   the owner's own tool, and the rest is machinery. */
const UNTRANSLATED = ['/admin-dashboard', '/user-dashboard', '/sign-up-login-screen', '/api'];

export function isTranslatablePath(path: string): boolean {
  const bare = stripLang(path);
  return !UNTRANSLATED.some((p) => bare === p || bare.startsWith(`${p}/`));
}

/** Collapses the whitespace JSX leaves behind, so one sentence is one key. */
export function normalise(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/* Text that would be nonsense to send to a translator, or damaging to replace.
   Numbers, prices, dates, codes and addresses all read the same in Lithuanian,
   and a machine asked to translate "01" or "hello@noventilabs.com" will
   sometimes return something else entirely. */
const NOT_WORDS = /^[\s\d\p{P}\p{S}]*$/u;
/* Addresses and numbers. There used to be an `[A-Z0-9_]{1,4}$` alternative here
   meant to spare acronyms like SEO and EU — but the pattern is
   case-insensitive, so it spared every word of four characters or fewer:
   "FREE", "NONE", "Home", "Team", "Free", "Next". They were excluded from
   translation entirely and stayed English on the Lithuanian pages, which is
   exactly the kind of quiet failure this whole design is supposed to avoid.

   Acronyms are handled where they belong instead — as entries in the dictionary
   that translate to themselves. "SEO" in Lithuanian is "SEO", and saying so in
   the dictionary is both true and checkable. */
const LOOKS_TECHNICAL = /^(https?:\/\/|www\.|mailto:|tel:|[\w.+-]+@[\w.-]+\.\w+$|[\d\s./:+-]+$)/i;
/* Machine labels the page prints as-is: SVC-01, WHY-04, INST-02. They read the
   same in every language, and offering them for translation only invites
   somebody to translate one. */
const SYSTEM_CODE = /^[A-Z]{2,6}[\s-]?\d{1,3}$/;

export function isTranslatable(text: string): boolean {
  const value = normalise(text);
  if (value.length < 2 || value.length > 5000) return false;
  if (NOT_WORDS.test(value)) return false;
  if (LOOKS_TECHNICAL.test(value)) return false;
  if (SYSTEM_CODE.test(value)) return false;
  // Needs at least one run of letters to be a word rather than a label.
  return /\p{L}{2,}/u.test(value);
}

export type Dictionary = Record<string, string>;
